import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { checkIsArchitect, checkIsBuildingInspector, checkIsClient } from "../../Utils/LoginUserRoleName";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Nullable } from "primereact/ts-helpers";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent, DropdownProps } from "primereact/dropdown";
import { confirmDialog } from "primereact/confirmdialog";
import { Checkbox } from "primereact/checkbox";

import TabFilter from "../../Components/TabFilter";
import NoDataCreated from "../../Components/NoDataCreated";
import PageLoader from "../../Components/PageLoader";
import ErrorPage from "../../Components/ErrorPage";
import GoBackButton from "../../Components/GoBackButton";
import DataListModalInput from "../../Components/DataListModal/DataListModalInput";
import DataListModalSearch from "../../Components/DataListModal/DataListModalSearch";
import DataListModalClose from "../../Components/DataListModal/DataListModalClose";
import CustomTableLoader from "../../Components/CustomTableLoader";
import DataException from "../../Components/DataException";
import DatePicker from "../../Components/DatePicker";
import TableActionButton from "../../Components/TableActionButton";
import Textarea from "../../Components/TextArea";
import TextInput from "../../Components/TextInput";
import TableButton from "../../Components/TableButton";
import FileInput from "../../Components/FileInput";
import ViewDataGroup from "../../Components/DataViewModal/ViewDataGroup";
import { goToTop } from "../../Components/goToTop";
import { checkFileOthers, checkModelSelected, checkText } from "../../Utils/Validator";
import { formatDate, parseStringToDate } from "../../Utils/getDate";

import { Project } from "../../Controllers/Types/Project/ProjectInterface";
import {
    BuildingInspectionCompany,
    BuildingControlQuotation as BuildingControlQuotationType,
    BuildingControlQuotationRequest,
    BuildingControlQuotationShareList
} from "../../Controllers/Types/BuildingControl/BuildingControlQuotationType";

import { fetchAllProjects } from "../../Controllers/Thunks/projectThunks";
import {
    fetchBuildingInspectionCompanies, fetchBuildingControlQuotations, fetchBuildingControlQuotationRequests
} from "../../Controllers/Thunks/BuildingControlThunks";
import { createBuildingControlQuotationAPI, createQuotationARCResponseAPI, createQuotationBICResponseAPI } from "../../Api/BuildingControlService/BuildingControlService";

const quotationStatusTabs = ['All', 'Pending', 'Finished'] as const;
type QuotationStatusTab = typeof quotationStatusTabs[number];

const BuildingControlQuotation: React.FC = () => {
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);

    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [activeTab, setActiveTab] = useState<QuotationStatusTab>('All');
    const [showBuildingInspectionCompanyModal, setShowBuildingInspectionCompanyModal] = useState<boolean>(false);
    const [showBuildingControlQuotationModal, setShowBuildingControlQuotationModal] = useState<boolean>(false);
    const [buildingInspectionCompanySearchKey, setBuildingInspectionCompanySearchKey] = useState<string>('');

    // State data
    const { allProjects: projects, loading: loadingProjects } = useAppSelector((state) => state.project);
    const {
        loadingBuildingInspectionCompanies, errorBuildingInspectionCompanies, buildingInspectionCompanies,
        loadingBuildingControlQuotations, errorBuildingControlQuotations, buildingControlQuotations,
        loadingBuildingControlQuotationRequests, errorBuildingControlQuotationRequests, buildingControlQuotationRequests,
    } = useAppSelector((state) => state.buildingControl);

    // Form fields
    const [project, setProject] = useState<Project | null>(null);
    const [projectName, setProjectName] = useState<string>('');
    const [projectAddress, setProjectAddress] = useState<string>('');
    const [selectedBuildingInspectionCompanies, setSelectedBuildingInspectionCompanies] = useState<BuildingInspectionCompany[]>([]);

    // Form errors
    const [projectError, setProjectError] = useState<string>('');
    const [projectNameError, setProjectNameError] = useState<string>('');
    const [projectAddressError, setProjectAddressError] = useState<string>('');
    const [selectedBuildingInspectionCompaniesError, setSelectedBuildingInspectionCompaniesError] = useState<string>('');

    const [selectedBuildingControlQuotation, setSelectedBuildingControlQuotation] = useState<BuildingControlQuotationType | null>(null);
    const [selectedBuildingControlQuotationRequest, setSelectedBuildingControlQuotationRequest] = useState<BuildingControlQuotationRequest | null>(null);
    const [selectedBuildingControlQuotationCompany, setSelectedBuildingControlQuotationCompany] = useState<BuildingControlQuotationShareList | null>(null);
    const [showResponseQuotationModal, setShowResponseQuotationModal] = useState<boolean>(false);
    const [showCompareQuotationModal, setShowCompareQuotationModal] = useState<boolean>(false);

    // Response fields
    const [responseNote, setResponseNote] = useState<string>('');
    const [responseFile, setResponseFile] = useState<File | null>(null);
    const [responseFileName, setResponseFileName] = useState<string>('');

    // Response errors
    const [responseNoteError, setResponseNoteError] = useState<string>('');
    const [responseFileError, setResponseFileError] = useState<string>('');

    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);
    const [isArchitect, setIsArchitect] = useState<boolean>(false);
    const [isClient, setIsClient] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult, architectResult, clientResult] = await Promise.all([
                checkIsBuildingInspector(),
                checkIsArchitect(),
                checkIsClient(),
            ]);

            setIsInspector(inspectorResult);
            setIsArchitect(architectResult);
            setIsClient(clientResult);
        };

        checkRole();
    }, []);
    /*  */

    /* Permissions */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForAddBuildingControlQuotation = useCanEdit("Building Control Quotation", "add_building_control_quotation");
    const hasPermissionForResponseBuildingControlQuotation = useCanEdit("Building Control Quotation", "response_building_control_quotation");
    const hasPermissionForCompareBuildingControlQuotation = useCanEdit("Building Control Quotation", "compare_building_control_quotation");
    /*  */

    const filteredBuildingControlQuotations = useMemo(() => {
        let filteredData = buildingControlQuotations;

        if (activeTab === 'All') return filteredData;
        if (activeTab === 'Pending') return filteredData.filter((e) => e.status === 'Pending');
        if (activeTab === 'Finished') return filteredData.filter((e) => e.status === 'Finished');

        return filteredData;
    }, [buildingControlQuotations, activeTab]);

    const filteredBuildingControlQuotationRequests = useMemo(() => {
        let filteredData = buildingControlQuotationRequests;

        if (activeTab === 'All') return filteredData;
        if (activeTab === 'Pending') return filteredData.filter((e) => e.status === 'Pending');
        if (activeTab === 'Finished') return filteredData.filter((e) => e.status === 'Finished');

        return filteredData;
    }, [buildingControlQuotationRequests, activeTab]);

    const filteredBuildingInspectionCompanies = useMemo(() => {
        const companies = buildingInspectionCompanies ?? [];
        const searchKey = buildingInspectionCompanySearchKey.trim().toLowerCase();

        if (!searchKey) {
            return companies;
        }

        return companies.filter((company) => {
            const companyName = company.company_name?.toLowerCase() ?? "";
            const email = company.email?.toLowerCase() ?? "";

            return companyName.includes(searchKey) || email.includes(searchKey);
        });
    }, [buildingInspectionCompanies, buildingInspectionCompanySearchKey]);

    const isProjectAlreadyQuoted = (projectId: number): boolean => {
        return buildingControlQuotations?.some(
            (quotation) => quotation?.project_id === projectId
        );
    };

    const projectOptions = projects?.map((project) => ({
        ...project,
        disabled: buildingControlQuotations?.some(
            (quotation) => quotation?.project_id === project.project_id
        ),
    }));

    const handleGetBuildingControlQuotations = useCallback(async () => {
        let role: 'architect' | 'client' = 'architect';

        if (isArchitect) {
            role = 'architect';
        } else if (isClient) {
            role = 'client';
        }

        try {
            await dispatch(fetchBuildingControlQuotations(role)).unwrap();
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch building control quotations.",
                life: 3000,
            });
        } finally {
            setInitialDataLoading(false);
        }
    }, [dispatch, isArchitect, isClient]);

    const handleGetBuildingControlQuotationRequests = useCallback(async () => {
        try {
            await dispatch(fetchBuildingControlQuotationRequests()).unwrap();
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch building control quotation requests.",
                life: 3000,
            });
        }
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchAllProjects());
        dispatch(fetchBuildingInspectionCompanies());
        if (isArchitect || isClient) handleGetBuildingControlQuotations();
        if (isInspector) handleGetBuildingControlQuotationRequests();
    }, [
        dispatch,
        handleGetBuildingControlQuotations,
        handleGetBuildingControlQuotationRequests,
        isArchitect,
        isInspector,
        isClient,
    ]);

    /* **************************************************************** Building inspection company */
    const handleShowBuildingInspectionCompanyModal = () => {
        setShowBuildingInspectionCompanyModal(true);
    };

    const handleCloseBuildingInspectionCompanyModal = (isClear: boolean) => {
        if (isClear) {
            setSelectedBuildingInspectionCompanies([]);
        }

        setBuildingInspectionCompanySearchKey('');
        setShowBuildingInspectionCompanyModal(false);
    };

    const handleSelectBuildingInspectionCompany = (company: BuildingInspectionCompany, isChecked: boolean) => {
        if (isChecked) {
            setSelectedBuildingInspectionCompanies((prevState) => [...prevState, company]);
        } else {
            setSelectedBuildingInspectionCompanies((prevState) =>
                prevState.filter(
                    (existingCompany) => existingCompany.id !== company.id
                )
            );
        }
    };

    const isSelectedBuildingInspectionCompany = (company: BuildingInspectionCompany): boolean =>
        selectedBuildingInspectionCompanies.some(
            (existingCompany) => existingCompany?.id === company?.id
        );

    const buildingInspectionCompanyModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={() => handleCloseBuildingInspectionCompanyModal(true)}
            />

            <Button
                label={`Add${selectedBuildingInspectionCompanies?.length > 0 ? `(${String(selectedBuildingInspectionCompanies?.length).padStart(2, '0')})` : ''}`}
                onClick={() => handleCloseBuildingInspectionCompanyModal(false)}
                className="custom_btn primary"
                disabled={selectedBuildingInspectionCompanies?.length === 0}
            />
        </div>
    );
    /* **************************************************************** */


    /* **************************************************************** Building control quotation */
    const handleFilterBuildingControlQuotation = (filterState: QuotationStatusTab) => {
        setActiveTab(filterState);
        goToTop();
    };

    const selectedProjectTemplate = (option: Project, props: DropdownProps) => {
        if (option) {
            return (
                <span>
                    <b>{option?.job_number}</b>&nbsp;-&nbsp;{option?.address}
                </span>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    const projectOptionTemplate = (option: Project & { disabled?: boolean }) => {
        return (
            <div
                className={`address_option_container d-flex justify-content-between align-items-center 
                    ${option?.project_type === "Commercial" ? "bg-blue" : "bg-purple"}
                    ${option?.disabled ? "disabled_option" : ""}
                `}
            >
                <span>
                    <b>{option?.job_number}</b>:&nbsp;
                    {option?.address}

                    {option?.disabled && (
                        <small className="ms-2 text-danger">
                            (Already quoted)
                        </small>
                    )}
                </span>

                <Tooltip
                    target={`.project_${option?.project_id}_type`}
                    position="left"
                />
                <span className={`project_type_badge project_${option?.project_id}_type`} data-pr-tooltip={option?.project_type === 'Commercial' ? 'Commercial Project' : 'Domestic Project'}>
                    {option?.project_type === 'Commercial' ? 'CP' : 'DP'}
                </span>
            </div>
        );
    };

    const handleCloseBuildingControlQuotationModal = () => {
        setShowBuildingControlQuotationModal(false);
        handleResetBuildingControlQuotationFields();
    };

    // Clear errors
    useEffect(() => setProjectError(""), [project]);
    useEffect(() => setProjectNameError(""), [projectName]);
    useEffect(() => setProjectAddressError(""), [projectAddress]);
    useEffect(() => setSelectedBuildingInspectionCompaniesError(""), [selectedBuildingInspectionCompanies]);

    // Check vaidation
    function checkCredentialsForBuildingControlQuotation(): boolean {
        const isValidProject = checkModelSelected(project !== null, true, setProjectError);
        const isValidProjectName = checkText(projectName ?? '', true, setProjectNameError);
        const isValidProjectAddress = checkText(projectAddress ?? '', true, setProjectAddressError);

        const isValidBuildingInspectionCompanies =
            (selectedBuildingInspectionCompanies?.length ?? 0) > 0;

        setSelectedBuildingInspectionCompaniesError(
            isValidBuildingInspectionCompanies
                ? ''
                : 'Select at least one building inspection company'
        );

        return (
            isValidProject &&
            isValidProjectName &&
            isValidProjectAddress &&
            isValidBuildingInspectionCompanies
        );
    }

    // Check API Error
    function showAPIErrorForBuildingControlQuotation(keyName: string, error: string): void {
        switch (keyName) {
            case "project_id":
                setProjectError(error);
                break;
            case "project_name":
                setProjectNameError(error);
                break;
            case "project_address":
                setProjectAddressError(error);
                break;
            case "share_list[]":
                setSelectedBuildingInspectionCompaniesError(error);
                break;
            default:
                break;
        }
    }

    const handleResetBuildingControlQuotationFields = () => {
        setProject(null);
        setProjectName('');
        setProjectAddress('');
        setSelectedBuildingInspectionCompanies([]);

        setProjectError('');
        setProjectNameError('');
        setProjectAddressError('');
        setSelectedBuildingInspectionCompaniesError('');
    };

    // --------------------------------------- Create building control quotation
    const handleAddBuildingControlQuotation = () => {
        setShowBuildingControlQuotationModal(true);
    };

    const handleCreateBuildingControlQuotation = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForBuildingControlQuotation()) return;

        setLoading(true);

        try {
            const project_id = project?.project_id ?? 0;
            const project_name = projectName ?? '';
            const project_address = projectAddress ?? '';
            const share_list = selectedBuildingInspectionCompanies?.map((e) => e.id);

            const response = await createBuildingControlQuotationAPI(
                project_id,
                project_name,
                project_address,
                share_list ?? [],
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlQuotations();
                handleCloseBuildingControlQuotationModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Building control quotation created successfully.",
                        life: 3000,
                    });
                }
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value], index) => (
                    showAPIErrorForBuildingControlQuotation(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error creating the building control quotation.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error adding the building control quotation:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding the building control quotation.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };
    // ---------------------------------------

    const buildingControlQuotationModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Add Building Control Quotation</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseBuildingControlQuotationModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const buildingControlQuotationModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary d-none d-sm-flex"
                onClick={handleCloseBuildingControlQuotationModal}
            />

            <Button
                label={loading ? "Processing" : "Create"}
                onClick={handleCreateBuildingControlQuotation}
                className="custom_btn primary"
                loading={loading}
            />
        </div>
    );

    const quotationTableARCResponseBody = (rowData: BuildingControlQuotationShareList, quotation: BuildingControlQuotationType) => {
        const isResponsed = rowData?.respone_data !== null;
        const isBICResponsed = rowData?.respone_data?.responce_added_arc === 0;

        return (
            <div className="tabel_btn_area justify-content-center mt-1 mb-1">
                <Button
                    severity={isBICResponsed ? "secondary" : isResponsed ? "success" : "secondary"}
                    label={isBICResponsed ? "View" : isResponsed ? "Responsed" : "Response"}
                    className="table_status_btn"
                    onClick={() => {
                        if (hasPermissionForResponseBuildingControlQuotation) {
                            handleResponseQuotationByARC(quotation, rowData);
                        }
                    }}
                    tooltipOptions={{ position: "left" }}
                    tooltip={isResponsed ? "Click to view/edit response" : "Click to add response"}
                    disabled={!hasPermissionForResponseBuildingControlQuotation}
                />
            </div>
        )
    }

    const getRowClassNameForQuotationShare = (data: BuildingControlQuotationShareList) => {
        const isApproved = data?.is_approved === 1;

        if (isApproved) {
            return "project_data_row success";
        } else {
            return "project_data_row secondary";
        }
    }
    /* **************************************************************** */


    /* **************************************************************** Building control quotation request */
    const quotationTableBICResponseBody = (rowData: BuildingControlQuotationRequest) => {
        const isResponsed = rowData?.responce_data !== null;
        const isARCResponsed = rowData?.responce_data?.responce_added_arc === 1;

        return (
            <div className="tabel_btn_area justify-content-center mt-1 mb-1">
                <Button
                    severity={isARCResponsed ? "secondary" : isResponsed ? "success" : "secondary"}
                    label={isARCResponsed ? "View" : isResponsed ? "Responsed" : "Response"}
                    className="table_status_btn"
                    onClick={() => {
                        if (hasPermissionForResponseBuildingControlQuotation) {
                            handleResponseQuotationByBIC(rowData);
                        }
                    }}
                    tooltipOptions={{ position: "left" }}
                    tooltip={isARCResponsed ? "Click to view response" : isResponsed ? "Click to view/edit response" : "Click to add response"}
                    disabled={!hasPermissionForResponseBuildingControlQuotation}
                />
            </div>
        )
    }
    /* **************************************************************** */


    /* **************************************************************** Response Quotation */
    const handleCloseResponseQuotationModal = () => {
        setShowResponseQuotationModal(false);
        handleResetResponseQuotationFields();
    };

    const handleResetResponseQuotationFields = () => {
        setSelectedBuildingControlQuotation(null);
        setSelectedBuildingControlQuotationCompany(null);
        setSelectedBuildingControlQuotationRequest(null);

        setResponseNote("");
        setResponseFile(null);
        setResponseFileName("");

        setResponseNoteError("");
        setResponseFileError("");
    };

    const handleUploadResponseFile = (file: File) => {
        setResponseFile(file);
        setResponseFileName(file.name);
    };

    const handleClearResponseFile = () => {
        setResponseFile(null);
        setResponseFileName("");
    };

    // Clear errors
    useEffect(() => setResponseNoteError(""), [responseNote]);
    useEffect(() => setResponseFileError(""), [responseFile]);

    // Check vaidation
    function checkCredentialsForResponseQuotation(): boolean {
        const isValidResponseNote = checkText(responseNote ?? '', true, setResponseNoteError);
        const isValidResponseFile = checkFileOthers(responseFile!, false, setResponseFileError);

        return (
            isValidResponseNote &&
            isValidResponseFile
        );
    }

    // Check API Error
    function showAPIErrorForResponseQuotation(keyName: string, error: string): void {
        switch (keyName) {
            case "reponce":
                setResponseNoteError(error);
                break;
            default:
                break;
        }
    }

    // --------------------------------------- Response quotation for ARC
    const handleResponseQuotationByARC = (quotation: BuildingControlQuotationType | null, company: BuildingControlQuotationShareList | null) => {
        if (!quotation || !company) return;
        setSelectedBuildingControlQuotation(quotation);
        setSelectedBuildingControlQuotationCompany(company);

        if (company?.respone_data) {
            setResponseNote(company?.respone_data?.reponce ?? '');
        }

        setShowResponseQuotationModal(true);
    };

    const handleCreateBuildingControlQuotationARCResponse = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForResponseQuotation() || !selectedBuildingControlQuotation || !selectedBuildingControlQuotationCompany) return;

        setLoading(true);

        try {
            const bc_quotation_id = selectedBuildingControlQuotation?.id ?? 0;
            const company_id = selectedBuildingControlQuotationCompany?.company_id ?? 0;

            const response = await createQuotationARCResponseAPI(
                bc_quotation_id,
                company_id,
                responseNote ?? '',
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlQuotations();
                handleCloseResponseQuotationModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Response added successfully.",
                        life: 3000,
                    });
                }
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value]) => (
                    showAPIErrorForResponseQuotation(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error adding response.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error adding response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding response.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };
    // ---------------------------------------


    // --------------------------------------- Response quotation for BIC
    const handleResponseQuotationByBIC = (quotationRequest: BuildingControlQuotationRequest | null) => {
        if (!quotationRequest) return;
        setSelectedBuildingControlQuotationRequest(quotationRequest);

        if (quotationRequest?.responce_data) {
            setResponseNote(quotationRequest?.responce_data?.reponce ?? '');
        }

        setShowResponseQuotationModal(true);
    };

    const handleCreateBuildingControlQuotationBICResponse = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForResponseQuotation() || !selectedBuildingControlQuotationRequest) return;

        setLoading(true);

        try {
            const bc_quotation_id = selectedBuildingControlQuotationRequest?.id ?? 0;

            const response = await createQuotationBICResponseAPI(
                bc_quotation_id,
                responseNote ?? '',
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlQuotationRequests();
                handleCloseResponseQuotationModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Response added successfully.",
                        life: 3000,
                    });
                }
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value]) => (
                    showAPIErrorForResponseQuotation(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error adding response.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error adding response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding response.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };
    // ---------------------------------------

    const responseQuotationModalHeader = () => {
        const isResponsed = (isArchitect && selectedBuildingControlQuotationCompany?.respone_data !== null) || (isInspector && selectedBuildingControlQuotationRequest?.responce_data !== null);
        const isARCResponsed = selectedBuildingControlQuotationRequest?.responce_data?.responce_added_arc === 1;
        const isBICResponsed = selectedBuildingControlQuotationCompany?.respone_data?.responce_added_arc === 0;

        return (
            <div className="custom_modal_header_inner">
                <h5 className="modal-title fs-5">{(isARCResponsed || isBICResponsed) ? 'View' : isResponsed ? 'Edit' : 'Add'} Response</h5>
                <button
                    type="button"
                    className="close_modal_btn p-ripple"
                    onClick={handleCloseResponseQuotationModal}
                >
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            </div>
        )
    };

    const responseQuotationModalFooter = () => {
        const isResponsed = (isArchitect && selectedBuildingControlQuotationCompany?.respone_data !== null) || (isInspector && selectedBuildingControlQuotationRequest?.responce_data !== null);
        const isARCResponsed = selectedBuildingControlQuotationRequest?.responce_data?.responce_added_arc === 1;
        const isBICResponsed = selectedBuildingControlQuotationCompany?.respone_data?.responce_added_arc === 0;
        const handleSubmitResponse = isArchitect ? handleCreateBuildingControlQuotationARCResponse : isInspector ? handleCreateBuildingControlQuotationBICResponse : () => { };

        return (
            <div className="custom_modal_footer">
                <Button
                    label="Close"
                    className="custom_btn secondary d-none d-sm-flex"
                    onClick={handleCloseResponseQuotationModal}
                />

                {((isInspector && !isARCResponsed) || (isArchitect && !isBICResponsed)) && (
                    <Button
                        label={loading ? "Processing" : isResponsed ? "Update" : "Submit"}
                        onClick={handleSubmitResponse}
                        className="custom_btn primary"
                        loading={loading}
                    />
                )}
            </div>
        )
    };
    /* **************************************************************** */


    /* **************************************************************** Compare quotation */
    const handleCompareQuotation = (quotation: BuildingControlQuotationType | null) => {
        if (!quotation) return;

        setSelectedBuildingControlQuotation(quotation);
        setShowCompareQuotationModal(true);
    };

    const handleCloseCompareQuotationModal = () => {
        setSelectedBuildingControlQuotation(null);
        setShowCompareQuotationModal(false);
    };
    /* **************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingBuildingControlQuotations && loadingBuildingControlQuotationRequests && initialDataLoading ? (
                    <PageLoader />
                ) : (errorBuildingControlQuotations || errorBuildingControlQuotationRequests) ? (
                    <ErrorPage errorMessage={errorBuildingControlQuotations ?? errorBuildingControlQuotationRequests ?? ''} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">Building control quotation </h4>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {quotationStatusTabs?.map((tab) => {
                                    const isDisabled = (isArchitect && buildingControlQuotations?.length === 0) || (isInspector && buildingControlQuotationRequests?.length === 0);
                                    const tabSeverety = tab === 'All' ? 'primary' : tab === 'Pending' ? 'warning' : tab === 'Finished' ? 'success' : '';

                                    return (
                                        <TabFilter
                                            key={tab}
                                            label={tab}
                                            isActive={activeTab === tab}
                                            onTabChange={() => handleFilterBuildingControlQuotation(tab)}
                                            tabClassName={tabSeverety}
                                            disabled={isDisabled}
                                        />
                                    )
                                })}
                            </div>

                            <div className="page_action_area">
                                {isArchitect && hasPermissionForAddBuildingControlQuotation && (
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddBuildingControlQuotation}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add quotation</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        {(isArchitect || isClient) && (
                            <div className="page_main_content_section">
                                {loadingBuildingControlQuotations && (
                                    <CustomTableLoader />
                                )}

                                {filteredBuildingControlQuotations && filteredBuildingControlQuotations?.length > 0 ? (
                                    <React.Fragment>
                                        {filteredBuildingControlQuotations?.map((quotation) => {
                                            return (
                                                <div key={`bcq_${quotation?.id}`} className="page_sub_content_section">
                                                    <div className="page_sub_content_header">
                                                        <div className="page_sub_content_sub">
                                                            <h4 className="page_sub_content_head text-capitalize">
                                                                <i className="bi bi-buildings me-2"></i>
                                                                {quotation?.project_name}<span className="text_subtext"> | {quotation?.project_address}</span>
                                                            </h4>
                                                        </div>

                                                        <div className="page_sub_content_header_btn_area">
                                                            {(isClient && hasPermissionForCompareBuildingControlQuotation) && (
                                                                <TableButton
                                                                    label="Compare"
                                                                    icon="bi bi-plus-lg"
                                                                    iconClassName="me-2"
                                                                    onClick={() => handleCompareQuotation(quotation)}
                                                                />
                                                            )}

                                                            {activeTab === 'All' && (
                                                                <span className={`sub_content_badge as_label ${quotation?.status === 'Pending' ? 'warning' : quotation?.status === 'Finished' ? 'success' : ''}`}>
                                                                    {quotation?.status}
                                                                </span>
                                                            )}

                                                            <span className="sub_content_badge">
                                                                {String(quotation?.quotation_share_list?.length ?? 0).padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="page_sub_content_body">
                                                        {quotation?.quotation_share_list && quotation?.quotation_share_list?.length > 0 ? (
                                                            <DataTable
                                                                key={`quotationShareList_${quotation?.id}`}
                                                                value={quotation?.quotation_share_list}
                                                                size="small"
                                                                dataKey="id"
                                                                className="page_table fs_data_table p-0"
                                                                rowClassName={getRowClassNameForQuotationShare}
                                                                rowHover
                                                                scrollable
                                                                tableStyle={{ minWidth: "50rem" }}
                                                                scrollHeight="490px"
                                                                loading={loadingBuildingControlQuotations}
                                                                emptyMessage="No companies found!"
                                                            >
                                                                <Column
                                                                    header="Company Name"
                                                                    headerClassName="text_purple"
                                                                    field="company.company_name"
                                                                    body={(rowData: BuildingControlQuotationShareList) => (
                                                                        <span className="text-capitalize">
                                                                            {rowData?.company?.company_name
                                                                                ? rowData?.company?.company_name
                                                                                : "---"}
                                                                        </span>
                                                                    )}
                                                                    style={{ width: "40%" }}
                                                                ></Column>

                                                                <Column
                                                                    header="Email"
                                                                    headerClassName="text_purple"
                                                                    field="company.email"
                                                                    body={(rowData: BuildingControlQuotationShareList) => (
                                                                        <span>
                                                                            {rowData?.company?.email
                                                                                ? rowData?.company?.email
                                                                                : "---"}
                                                                        </span>
                                                                    )}
                                                                    style={{ width: "30%" }}
                                                                ></Column>

                                                                {isArchitect && (
                                                                    <Column
                                                                        header="Response"
                                                                        alignHeader={'center'}
                                                                        headerClassName="text_purple"
                                                                        body={(rowData: BuildingControlQuotationShareList) => quotationTableARCResponseBody(rowData, quotation)}
                                                                        style={{ width: "30%" }}
                                                                    ></Column>
                                                                )}
                                                            </DataTable>
                                                        ) : (
                                                            <NoDataCreated
                                                                containerClassname="pos_rel sub"
                                                                message={`The ${quotation?.project_name} quotation has not been shared with any companies yet. ${hasPermissionForAddBuildingControlQuotation ? 'To share with companies, click the Add button' : ''}`}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </React.Fragment>
                                ) : (
                                    <NoDataCreated message={`No ${activeTab !== 'All' ? activeTab.toLowerCase() : ''} building control quotation found`} />
                                )}
                            </div>
                        )}

                        {isInspector && (
                            <div className="page_main_content_section">
                                <DataTable
                                    key={`quotationRequest`}
                                    value={filteredBuildingControlQuotationRequests}
                                    size="small"
                                    dataKey="id"
                                    className="page_table fs_data_table p-0"
                                    rowClassName={() => 'project_data_row secondary'}
                                    rowHover
                                    scrollable
                                    tableStyle={{ minWidth: "50rem" }}
                                    scrollHeight="490px"
                                    loading={loadingBuildingControlQuotations}
                                    emptyMessage="No building control quotation requests found!"
                                >
                                    <Column
                                        header="Project"
                                        headerClassName="text_purple"
                                        field="project_name"
                                        body={(rowData: BuildingControlQuotationRequest) => (
                                            <span className="text-capitalize">
                                                {rowData?.project_name
                                                    ? rowData?.project_name
                                                    : "---"}
                                            </span>
                                        )}
                                        style={{ width: "30%" }}
                                    ></Column>

                                    <Column
                                        header="Address"
                                        headerClassName="text_purple"
                                        field="project_address"
                                        body={(rowData: BuildingControlQuotationRequest) => (
                                            <span className="text-capitalize">
                                                {rowData?.project_address
                                                    ? rowData?.project_address
                                                    : "---"}
                                            </span>
                                        )}
                                        style={{ width: "30%" }}
                                    ></Column>

                                    <Column
                                        header="Response"
                                        alignHeader={'center'}
                                        headerClassName="text_purple"
                                        body={(rowData: BuildingControlQuotationRequest) => quotationTableBICResponseBody(rowData)}
                                        style={{ width: "30%" }}
                                    ></Column>
                                </DataTable>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Project progress modal */}
            <Dialog
                visible={showBuildingControlQuotationModal}
                headerClassName="custom_modal_header"
                header={buildingControlQuotationModalHeader}
                footer={buildingControlQuotationModalFooter}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseBuildingControlQuotationModal}
                dismissableMask
                closeOnEscape={false}
            >
                <div className="custom_modal_body p-2 p-sm-3">
                    <div className="row">
                        {/* Project */}
                        <div className="col-12">
                            <div className="page_form_group">
                                <label htmlFor="Project" className="custom_form_label is_required">
                                    Project
                                </label>

                                <Dropdown
                                    id="project"
                                    value={project}
                                    onChange={(e: DropdownChangeEvent) => setProject(e.value)}
                                    options={projectOptions}
                                    optionLabel="job_number"
                                    optionDisabled="disabled"
                                    valueTemplate={selectedProjectTemplate}
                                    itemTemplate={projectOptionTemplate}
                                    emptyFilterMessage="No project(s) found!"
                                    placeholder="Select project"
                                    className="form_dropdown address_selection w-100"
                                    filter
                                    showClear
                                    filterInputAutoFocus
                                    filterBy="job_number,address"
                                    filterPlaceholder="Search by address/job no."
                                    panelClassName="address_dropdown_panel"
                                    loading={loadingProjects}
                                />

                                {projectError && (
                                    <small className="form_error_msg">{projectError}</small>
                                )}
                            </div>
                        </div>

                        {/* Project Name */}
                        <div className="col-12">
                            <TextInput
                                id="projectName"
                                label="Project Name"
                                labelHtmlFor="projectName"
                                required={true}
                                value={projectName}
                                placeholder="Project Name"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
                                error={projectNameError}
                            />
                        </div>

                        {/* Project Address */}
                        <div className="col-12">
                            <Textarea
                                key={`projectAddress`}
                                id="projectAddress"
                                label="Project Address"
                                labelHtmlFor="projectAddress"
                                required={true}
                                value={projectAddress}
                                placeholder="Project Address"
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setProjectAddress(e.target.value)}
                                error={projectAddressError}
                            />
                        </div>

                        {/* Building Inspection Companies */}
                        <div className="col-12">
                            <div className="page_form_group mb-2">
                                <label
                                    htmlFor="buildingInspectionCompanies"
                                    className="custom_form_label is_required"
                                >
                                    Building Inspection Companies
                                </label>
                                <div className="input_with_button_area">
                                    <InputText
                                        key={`buildingInspectionCompanies`}
                                        id="buildingInspectionCompanies"
                                        value={selectedBuildingInspectionCompanies?.length > 0 ?
                                            selectedBuildingInspectionCompanies.map(company => company.company_name).join(', ') : ""}
                                        className="custom_form_input has_add_value"
                                        placeholder="Click to select company/companies."
                                        readOnly={true}
                                        autoComplete="off"
                                        onClick={handleShowBuildingInspectionCompanyModal}
                                    />
                                    <button
                                        className="add_value_btn p-ripple"
                                        onClick={handleShowBuildingInspectionCompanyModal}
                                    >
                                        {selectedBuildingInspectionCompanies?.length === 0 ? (
                                            <React.Fragment>
                                                <i className="bi bi-plus-lg"></i>
                                                <span>Select</span>
                                            </React.Fragment>
                                        ) : (
                                            <React.Fragment>
                                                <i className="bi bi-pencil-square"></i>
                                                <span>Change</span>
                                            </React.Fragment>
                                        )}
                                        <Ripple />
                                    </button>
                                </div>

                                {selectedBuildingInspectionCompaniesError && (
                                    <small className="form_error_msg">{selectedBuildingInspectionCompaniesError}</small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>


            {/* Building Inspection Companies Modal */}
            <Dialog
                visible={showBuildingInspectionCompanyModal}
                footer={buildingInspectionCompanyModalFooter}
                headerClassName="d-none"
                dismissableMask
                draggable={false}
                className="custom_modal_dialog modal_dialog_sm data_select_modal"
                contentClassName="data_select_modal_content"
                onHide={() => handleCloseBuildingInspectionCompanyModal(true)}
            >
                <div className="custom_modal_body data_select_modal_area">
                    <div className="data_select_modal_header">
                        <DataListModalSearch
                            key={`buildingInspectionCompanySearchKey`}
                            inputId="buildingInspectionCompanySearchKey"
                            value={buildingInspectionCompanySearchKey}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBuildingInspectionCompanySearchKey(e.target.value)}
                            placeholder="Search company by name/email"
                            disabled={buildingInspectionCompanies?.length === 0}
                            onClear={() => setBuildingInspectionCompanySearchKey("")}
                        />

                        <DataListModalClose
                            onClose={() => handleCloseBuildingInspectionCompanyModal(true)}
                        />
                    </div>

                    {loadingBuildingInspectionCompanies ? (
                        <CustomTableLoader />
                    ) : errorBuildingInspectionCompanies ? (
                        <div className="data_select_modal_body custom">
                            <DataException
                                containerClassName="custom_height"
                                message={errorBuildingInspectionCompanies}
                            />
                        </div>
                    ) : (
                        <div className="data_select_modal_body custom">
                            {filteredBuildingInspectionCompanies && filteredBuildingInspectionCompanies?.length > 0 ? (
                                <div className="data_select_option_area">
                                    {filteredBuildingInspectionCompanies?.map((company) => (
                                        <div key={`buildingInspectionCompany_${company?.id}`} className="data_select_option_check_area">
                                            <Checkbox
                                                onChange={(e) => handleSelectBuildingInspectionCompany(company, e.checked || false)}
                                                checked={isSelectedBuildingInspectionCompany(company)}
                                            ></Checkbox>

                                            <div
                                                className={`data_select_option p-ripple 
                                                        ${isSelectedBuildingInspectionCompany(company) ? "selected" : ""}
                                                    `}
                                                onClick={() => handleSelectBuildingInspectionCompany(company, !isSelectedBuildingInspectionCompany(company))}
                                            >
                                                <span>
                                                    {company?.company_name}
                                                    <span className="text_subtext lower">&nbsp;|&nbsp;{company?.email ?? ''}</span>
                                                </span>
                                                <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <DataException
                                    containerClassName="custom_height"
                                    message={buildingInspectionCompanySearchKey ? 'The company you were looking for is not found!' : `No companies found.`}
                                />
                            )}
                        </div>
                    )}
                </div>
            </Dialog>


            {/* Building Control Quotation Response Modal */}
            <Dialog
                visible={showResponseQuotationModal}
                headerClassName="custom_modal_header"
                header={responseQuotationModalHeader}
                footer={responseQuotationModalFooter}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseResponseQuotationModal}
                dismissableMask
                draggable={false}
                closeOnEscape={false}
            >
                <div className="custom_modal_body p-2 p-sm-3">
                    <div className="row">
                        {isArchitect ? (
                            <>
                                {/* Project details */}
                                <div className="col-12 mb-3">
                                    <h4 className="view_data_head">
                                        <i className="bi bi-buildings me-2"></i>
                                        Project details
                                    </h4>

                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Project"
                                            value={selectedBuildingControlQuotation?.project_name}
                                        />
                                        <ViewDataGroup
                                            title="Address"
                                            value={selectedBuildingControlQuotation?.project_address}
                                        />
                                    </article>
                                </div>

                                {/* Inspection company details */}
                                <div className="col-12 mb-4">
                                    <h4 className="view_data_head">
                                        <i className="bi bi-person-vcard me-2"></i>
                                        Inspection company details
                                    </h4>

                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Company name"
                                            value={selectedBuildingControlQuotationCompany?.company?.company_name}
                                        />
                                        <ViewDataGroup
                                            title="Email"
                                            value={selectedBuildingControlQuotationCompany?.company?.email}
                                        />
                                        <ViewDataGroup
                                            title="Address"
                                            value={selectedBuildingControlQuotationCompany?.company?.address}
                                        />
                                    </article>
                                </div>
                            </>
                        ) : isInspector ? (
                            <>
                                {/* Project details */}
                                <div className="col-12 mb-3">
                                    <h4 className="view_data_head">
                                        <i className="bi bi-buildings me-2"></i>
                                        Project details
                                    </h4>

                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Project"
                                            value={selectedBuildingControlQuotationRequest?.project_name}
                                        />
                                        <ViewDataGroup
                                            title="Address"
                                            value={selectedBuildingControlQuotationRequest?.project_address}
                                        />
                                    </article>
                                </div>
                            </>
                        ) : null}

                        {(isArchitect && (selectedBuildingControlQuotationCompany?.respone_data === null || selectedBuildingControlQuotationCompany?.respone_data?.responce_added_arc === 1)) ||
                            (isInspector && (selectedBuildingControlQuotationRequest?.responce_data === null || selectedBuildingControlQuotationRequest?.responce_data?.responce_added_arc === 0)) ? (
                            <>
                                {/* Response Note */}
                                <div className="col-12">
                                    <Textarea
                                        key={`responseNote`}
                                        id="responseNote"
                                        label="Response Note"
                                        labelHtmlFor="responseNote"
                                        required={true}
                                        value={responseNote}
                                        placeholder="Write your response here.."
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setResponseNote(e.target.value)}
                                        error={responseNoteError}
                                    />
                                </div>

                                {/* File */}
                                <div className="col-12">
                                    <FileInput
                                        key={`responseFile`}
                                        id="responseFile"
                                        label="File"
                                        labelHtmlFor="responseFile"
                                        required={false}
                                        canUploadImage
                                        value={responseFileName}
                                        onUpload={handleUploadResponseFile}
                                        onClear={handleClearResponseFile}
                                        toast={toast}
                                        error={responseFileError}
                                        containerClassName="mb-0"
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="col-12">
                                <h4 className="view_data_head">
                                    <i className="bi bi-buildings me-2"></i>
                                    Response
                                </h4>

                                {isArchitect ? (
                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Note"
                                            value={selectedBuildingControlQuotationCompany?.respone_data?.reponce}
                                        />
                                    </article>
                                ) : isInspector ? (
                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Note"
                                            value={selectedBuildingControlQuotationRequest?.responce_data?.reponce}
                                        />
                                    </article>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
        </React.Fragment >
    );
};

export default BuildingControlQuotation;